import { Fragment, useMemo } from 'react';
import { ScheduleRequestEventData } from '@frontend/schedule-helpers';
import { OverlappingEvents } from '../overlapping-events';
import { useCalendarView } from '../providers';
import { EventData } from '../types';
import { groupOverlappingEvents } from '../utils';
import { CalendarEventCardV3 } from './calendar-event-card';

interface EventCardsProps {
  events: EventData[];
  locationName: string;
  calendarDate?: string;
  draggedEventId?: string | null;
  isDragging?: boolean;
  hasPendingDrop?: boolean;
  selectedScheduleRequestEventData: ScheduleRequestEventData | null;
  setSelectedScheduleRequestEventData: (request: ScheduleRequestEventData | null) => void;
}

export const EventCards: React.FC<EventCardsProps> = ({
  events,
  locationName,
  calendarDate,
  draggedEventId,
  isDragging = false,
  hasPendingDrop = false,
  selectedScheduleRequestEventData,
  setSelectedScheduleRequestEventData,
}) => {
  const { visibleLaneCount } = useCalendarView(['visibleLaneCount']);
  const eventsGroupedByOverlap = useMemo(() => groupOverlappingEvents(events, true), [events]);

  return eventsGroupedByOverlap.map((groupEvents, groupIndex) => {
    /*
      Override the visibleLaneCount to show all out of office and break events.
      This is to ensure that the out of office events are visible on calendar lane instead of carousel.
      It will be rare for a lane to have more than 2 out of office events at a given time. In normal case, it will be max 2, one for provider and one for location.
    */
    const outEventsCount = groupEvents.filter(
      (groupEvent) => groupEvent.eventType === 'outOfOffice' || groupEvent.eventType === 'break'
    ).length;
    const effectiveVisibleLaneCount = Math.max(visibleLaneCount, outEventsCount);

    return (
      <Fragment key={`${groupEvents[0].eventId}-${groupIndex}`}>
        {groupEvents.map((event, index) => (
          <CalendarEventCardV3
            key={`${event.eventId}-${index}`}
            id={event.eventId}
            title={event.name}
            startDate={event.startDate}
            calendarDate={calendarDate}
            startHour={event.startHour}
            endHour={event.endHour}
            type={event.eventType}
            laneCount={event.laneCount}
            laneIndex={event.laneIndex}
            entityId={event.entityId}
            entityName={event.entityName}
            locationName={locationName}
            locationId={event.locationId}
            visibleLaneCount={effectiveVisibleLaneCount}
            personStatus={event.personStatus}
            eventStatus={event.status}
            appointmentType={event.type}
            eventTypeColor={event.displayOptions?.primaryColor}
            backgroundColor={event.displayOptions?.backgroundColor}
            isDragging={isDragging}
            isDraggingCurrentCard={event.eventId === draggedEventId}
            hasPendingDrop={hasPendingDrop}
            scheduleRequestEventData={event.request}
            selectedScheduleRequestEventData={selectedScheduleRequestEventData}
            setSelectedScheduleRequestEventData={setSelectedScheduleRequestEventData}
          />
        ))}
        <OverlappingEvents overlappingEvents={groupEvents} visibleLaneCount={effectiveVisibleLaneCount} />
      </Fragment>
    );
  });
};
